import React, { useState, useEffect, useRef, useCallback } from 'react';
import { acceptanceCriteriaAPI, analyticalMethodsAPI, cleaningConfigAPI, macoAPI, settingsAPI, commercialConfigAPI } from '../services/api';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import { stampPdfFooter } from '../utils/reportProfiles';
import useApprovalRights from '../hooks/useApprovalRights';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import { ActionsMenu } from '../components/ActionsMenu';
import VersionHistoryModal from '../components/VersionHistoryModal';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import {
  FaSave, FaCheck, FaFileUpload, FaPlus, FaTrash, FaInfoCircle,
  FaFileExcel, FaFilePdf, FaDownload, FaUpload, FaSync, FaEdit, FaTrashAlt,
  FaPaperclip, FaEye, FaTimes, FaVial, FaTint, FaCog, FaClipboardCheck,
} from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import StatusBadge from '../components/StatusBadge';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// ── Constants ──────────────────────────────────────────────────────────────────
const SWAB_AREA_OPTIONS    = [25, 100];
const SWAB_DILUENT_OPTIONS = [5, 10];
const DEFAULT_SOLVENTS     = ['WFI', 'Purified Water', 'Placebo', 'Other Solvent'];
const DEFAULT_AREA_CLASSES = ['Grade A', 'Grade B', 'Grade C', 'Grade D', 'ISO 5', 'ISO 6', 'ISO 7', 'ISO 8'];


const APPROVAL_DECISIONS = [
  { value:'approve', label:'Approve' },
  { value:'return',  label:'Return to Draft' },
  { value:'reject',  label:'Reject' },
];

// ── Calculations ───────────────────────────────────────────────────────────────
// Swab API limit is SAME for ALL parts: MAR(µg/cm²) × swabArea(cm²) / diluent(mL) → µg/mL
const calcSwabApiLimit = (mar, swabArea, swabDiluent) => {
  const m = parseFloat(mar), a = parseFloat(swabArea), v = parseFloat(swabDiluent);
  if (!m || !a || !v) return null;
  return m * a / v;
};
// Rinse API limit varies per part: MAR × rinse_sa / rinse_vol → ppm (µg/mL)
const calcRinseLimit = (row, mar) => {
  const m  = parseFloat(mar);
  const sa = parseFloat(row.rinse_sa);
  const v  = parseFloat(row.rinse_vol);
  if (!m || !sa || !v) return null;
  return m * sa / v;
};
const fmtLimit = (val) => {
  if (val === null || val === undefined) return '—';
  if (val < 0.0001) return val.toExponential(3);
  return val.toFixed(4);
};

// ── Row templates (NO per-row MAR or ref SA in swab) ──────────────────────────
const emptySwabRow = (part) => ({
  part_id:        part.part_id,
  part_name:      part.part_name,
  equipment_name: part.equipment_name,
  material:       part.material_of_construction || '',
  area_class:     '',
  amv_reference:  '',
  amv_files:      [],
  visual_limit:   'Pass',
  remarks:        '',
});
const emptyRinseRow = (part) => ({
  part_id:          part.part_id,
  part_name:        part.part_name,
  equipment_name:   part.equipment_name,
  material:         part.material_of_construction || '',
  surface_area_ref: part.surface_area || '',
  rinse_solvent:    '',
  rinse_sa:         part.surface_area || '',
  rinse_vol:        '',
  rinse_notes:      '',
  amv_reference:    '',
  amv_files:        [],
  visual_limit:     'Pass',
  remarks:          '',
});

// AMV (Analytical Method Validation) reference — manual reference no. + PDF attachment.
// Reference no. doubles as the future LIMS lookup key once LIMS integration is wired up.
const AmvCell = ({ reference, files=[], onReference, onUpload, onRemove, disabled, uploading }) => {
  const inputRef = useRef(null);
  return (
    <div style={{ minWidth:'150px' }}>
      <input className="form-control" style={{ width:'100%', fontSize:'var(--fs-sm)', padding:'2px 6px', marginBottom:'3px' }}
        value={reference} disabled={disabled} onChange={e=>onReference(e.target.value)} placeholder="AMV ref. no."/>
      {files.map(f => (
        <div key={f.filename} style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'2px', fontSize:'var(--fs-xs)' }}>
          <a href={`${API_ORIGIN}/uploads/${f.filename}`} target="_blank" rel="noopener noreferrer" title={f.originalname}
            style={{ color:'var(--primary-color)', textDecoration:'none', maxWidth:'110px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'inline-block' }}>
            <FaEye style={{ marginRight:'3px', fontSize:'var(--fs-2xs)' }}/>{f.originalname || f.filename}
          </a>
          {!disabled && (
            <button type="button" onClick={()=>onRemove(f.filename)} title="Remove file"
              style={{ border:'none', background:'none', cursor:'pointer', color:'var(--danger-color)', padding:'0 2px', lineHeight:1, fontSize:'var(--fs-2xs)' }}>
              <FaTimes/>
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <>
          <input ref={inputRef} type="file" accept="application/pdf" style={{ display:'none' }}
            onChange={e=>{ if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value=''; }}/>
          <button type="button" onClick={()=>inputRef.current?.click()} disabled={uploading}
            style={{ fontSize:'var(--fs-xs)', padding:'2px 7px', borderRadius:'4px', border:'1px dashed var(--border-color)', background:'transparent', cursor:'pointer', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:'3px', whiteSpace:'nowrap' }}>
            <FaPaperclip style={{ fontSize:'var(--fs-2xs)' }}/>{uploading ? 'Uploading…' : 'Attach PDF'}
          </button>
        </>
      )}
    </div>
  );
};

const Num = ({ value, onChange, disabled, placeholder, width='80px', step='0.001' }) => (
  <input type="number" min="0" step={step} className="form-control"
    style={{ width, padding:'3px 6px', fontSize:'var(--fs-md)' }}
    value={value ?? ''} disabled={disabled}
    onChange={e => onChange(e.target.value)} placeholder={placeholder}/>
);

// ── Setup sub-tables ───────────────────────────────────────────────────────────
const SolventTable = ({ solvents, setSolvents, plantCode }) => {
  const addRow = () => setSolvents(prev => [...prev, {
    _new:true, solvent_name:'', toc_limit:'', toc_unit:'ppb',
    ph_min:'', ph_max:'', conductivity_limit:'', conductivity_unit:'µS/cm',
    bet_limit:'', bet_unit:'EU/mL', microbial_limit:'', microbial_unit:'CFU/mL',
  }]);
  const update = (i,f,v) => setSolvents(prev=>{const n=[...prev];n[i]={...n[i],[f]:v,_dirty:true};return n;});
  const saveRow = async (i) => {
    const row = solvents[i];
    if (!row._new && !row._dirty) return toast.info('No changes were made — nothing to save.');
    if (!row.solvent_name.trim()) return toast.error('Solvent name required');
    try {
      const res = await acceptanceCriteriaAPI.saveSolvent({...row, plant_code:plantCode});
      setSolvents(prev=>{const n=[...prev];n[i]={...res.data.data};return n;});
      toast.success('Solvent criteria saved');
    } catch { toast.error('Failed to save'); }
  };
  const deleteRow = async (i) => {
    const row = solvents[i];
    if (row._new){setSolvents(prev=>prev.filter((_,idx)=>idx!==i));return;}
    try{await acceptanceCriteriaAPI.deleteSolvent(row.id);setSolvents(prev=>prev.filter((_,idx)=>idx!==i));}
    catch{toast.error('Failed to deactivate');}
  };
  return (
    <div style={{marginBottom:'28px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px'}}>
        <h3 style={{fontWeight:700,fontSize:'var(--fs-lg)',margin:0}}>Rinsing Solvent Criteria</h3>
        <button className="btn btn-outline btn-sm" onClick={addRow}><FaPlus style={{marginRight:'4px'}}/>Add Solvent</button>
      </div>
      <p style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)',marginBottom:'8px'}}>Limits per solvent — auto-populate in Rinse Acceptance Criteria.</p>
      <FloatingScrollWrapper>
        <table style={{minWidth:'1050px'}}>
          <thead>
            <tr>
              <th style={{minWidth:'140px'}}>Solvent Name</th>
              <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #6366f1 8%, var(--bg-color))'}}>TOC</th>
              <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #0891b2 8%, var(--bg-color))'}}>pH Range</th>
              <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #0284c7 8%, var(--bg-color))'}}>Conductivity</th>
              <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #dc2626 8%, var(--bg-color))'}}>BET / Endotoxin</th>
              <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #16a34a 8%, var(--bg-color))'}}>Microbial</th>
              <th style={{minWidth:'60px'}}/>
            </tr>
            <tr>
              <th/>
              <th style={{minWidth:'80px',background:'color-mix(in srgb, #6366f1 5%, var(--bg-color))'}}>Limit</th>
              <th style={{minWidth:'70px',background:'color-mix(in srgb, #6366f1 5%, var(--bg-color))'}}>Unit</th>
              <th style={{minWidth:'72px',background:'color-mix(in srgb, #0891b2 5%, var(--bg-color))'}}>Min</th>
              <th style={{minWidth:'72px',background:'color-mix(in srgb, #0891b2 5%, var(--bg-color))'}}>Max</th>
              <th style={{minWidth:'80px',background:'color-mix(in srgb, #0284c7 5%, var(--bg-color))'}}>Limit</th>
              <th style={{minWidth:'80px',background:'color-mix(in srgb, #0284c7 5%, var(--bg-color))'}}>Unit</th>
              <th style={{minWidth:'80px',background:'color-mix(in srgb, #dc2626 5%, var(--bg-color))'}}>Limit</th>
              <th style={{minWidth:'70px',background:'color-mix(in srgb, #dc2626 5%, var(--bg-color))'}}>Unit</th>
              <th style={{minWidth:'80px',background:'color-mix(in srgb, #16a34a 5%, var(--bg-color))'}}>Limit</th>
              <th style={{minWidth:'80px',background:'color-mix(in srgb, #16a34a 5%, var(--bg-color))'}}>Unit</th>
              <th/>
            </tr>
          </thead>
          <tbody>
            {solvents.length===0&&<tr><td colSpan={12} style={{textAlign:'center',color:'var(--text-secondary)',padding:'20px'}}>No solvents defined.</td></tr>}
            {solvents.map((row,i)=>(
              <tr key={row.id||`new-${i}`}>
                <td>
                  <input className="form-control" style={{width:'130px',fontSize:'var(--fs-md)',padding:'3px 6px'}}
                    value={row.solvent_name} onChange={e=>update(i,'solvent_name',e.target.value)} placeholder="e.g. WFI" list="solvent-list"/>
                  <datalist id="solvent-list">{DEFAULT_SOLVENTS.map(s=><option key={s} value={s}/>)}</datalist>
                </td>
                <td style={{background:'color-mix(in srgb, #6366f1 3%, var(--card-bg))'}}><Num value={row.toc_limit} onChange={v=>update(i,'toc_limit',v)} placeholder="500"/></td>
                <td style={{background:'color-mix(in srgb, #6366f1 3%, var(--card-bg))'}}>
                  <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'3px 4px',width:'65px'}} value={row.toc_unit||'ppb'} onChange={e=>update(i,'toc_unit',e.target.value)}>
                    {['ppb','µg/L','ppm','mg/L'].map(u=><option key={u}>{u}</option>)}
                  </select>
                </td>
                <td style={{background:'color-mix(in srgb, #0891b2 3%, var(--card-bg))'}}><Num value={row.ph_min} onChange={v=>update(i,'ph_min',v)} placeholder="5.0" step="0.1"/></td>
                <td style={{background:'color-mix(in srgb, #0891b2 3%, var(--card-bg))'}}><Num value={row.ph_max} onChange={v=>update(i,'ph_max',v)} placeholder="7.0" step="0.1"/></td>
                <td style={{background:'color-mix(in srgb, #0284c7 3%, var(--card-bg))'}}><Num value={row.conductivity_limit} onChange={v=>update(i,'conductivity_limit',v)} placeholder="1.3" step="0.01"/></td>
                <td style={{background:'color-mix(in srgb, #0284c7 3%, var(--card-bg))'}}>
                  <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'3px 4px',width:'70px'}} value={row.conductivity_unit||'µS/cm'} onChange={e=>update(i,'conductivity_unit',e.target.value)}>
                    {['µS/cm','mS/cm'].map(u=><option key={u}>{u}</option>)}
                  </select>
                </td>
                <td style={{background:'color-mix(in srgb, #dc2626 3%, var(--card-bg))'}}><Num value={row.bet_limit} onChange={v=>update(i,'bet_limit',v)} placeholder="0.25" step="0.01"/></td>
                <td style={{background:'color-mix(in srgb, #dc2626 3%, var(--card-bg))'}}>
                  <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'3px 4px',width:'70px'}} value={row.bet_unit||'EU/mL'} onChange={e=>update(i,'bet_unit',e.target.value)}>
                    {['EU/mL','IU/mL'].map(u=><option key={u}>{u}</option>)}
                  </select>
                </td>
                <td style={{background:'color-mix(in srgb, #16a34a 3%, var(--card-bg))'}}><Num value={row.microbial_limit} onChange={v=>update(i,'microbial_limit',v)} placeholder="10"/></td>
                <td style={{background:'color-mix(in srgb, #16a34a 3%, var(--card-bg))'}}>
                  <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'3px 4px',width:'78px'}} value={row.microbial_unit||'CFU/mL'} onChange={e=>update(i,'microbial_unit',e.target.value)}>
                    {['CFU/mL','CFU/100mL'].map(u=><option key={u}>{u}</option>)}
                  </select>
                </td>
                <td style={{textAlign:'center'}}>
                  <div style={{display:'flex',gap:'4px',justifyContent:'center'}}>
                    <button className="btn btn-outline btn-sm" style={{padding:'3px 8px',fontSize:'var(--fs-sm)'}} onClick={()=>saveRow(i)}><FaSave/></button>
                    <button className="btn btn-sm" title="Deactivate" style={{padding:'3px 8px',fontSize:'var(--fs-sm)',background:'none',color:'var(--danger-color)',border:'1px solid var(--danger-border)'}} onClick={()=>deleteRow(i)}><FaTrash/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </FloatingScrollWrapper>
    </div>
  );
};

const AreaClassTable = ({ areaClasses, setAreaClasses, plantCode }) => {
  const addRow = () => setAreaClasses(prev=>[...prev,{_new:true,area_class:'',bioburden_limit:'',bioburden_unit:'CFU/swab'}]);
  const update = (i,f,v) => setAreaClasses(prev=>{const n=[...prev];n[i]={...n[i],[f]:v,_dirty:true};return n;});
  const saveRow = async (i) => {
    const row = areaClasses[i];
    if (!row._new && !row._dirty) return toast.info('No changes were made — nothing to save.');
    if (!row.area_class.trim()) return toast.error('Area class required');
    try {
      const res = await acceptanceCriteriaAPI.saveAreaClass({...row,plant_code:plantCode});
      setAreaClasses(prev=>{const n=[...prev];n[i]={...res.data.data};return n;});
      toast.success('Area class saved');
    } catch { toast.error('Failed to save'); }
  };
  const deleteRow = async (i) => {
    const row = areaClasses[i];
    if (row._new){setAreaClasses(prev=>prev.filter((_,idx)=>idx!==i));return;}
    try{await acceptanceCriteriaAPI.deleteAreaClass(row.id);setAreaClasses(prev=>prev.filter((_,idx)=>idx!==i));}
    catch{toast.error('Failed to deactivate');}
  };
  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px'}}>
        <h3 style={{fontWeight:700,fontSize:'var(--fs-lg)',margin:0}}>Area Classification — Bioburden Limits (Swab)</h3>
        <button className="btn btn-outline btn-sm" onClick={addRow}><FaPlus style={{marginRight:'4px'}}/>Add Class</button>
      </div>
      <p style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)',marginBottom:'8px'}}>Auto-fills bioburden limit in the Swab tab when area class is selected per equipment.</p>
      <FloatingScrollWrapper>
      <table style={{width:'100%',maxWidth:'500px'}}>
        <thead><tr>
          <th style={{minWidth:'140px'}}>Area Class</th>
          <th style={{minWidth:'110px'}}>Bioburden Limit</th>
          <th style={{minWidth:'100px'}}>Unit</th>
          <th style={{minWidth:'80px'}}/>
        </tr></thead>
        <tbody>
          {areaClasses.length===0&&<tr><td colSpan={4} style={{textAlign:'center',color:'var(--text-secondary)',padding:'16px'}}>No classes defined.</td></tr>}
          {areaClasses.map((row,i)=>(
            <tr key={row.id||`new-${i}`}>
              <td>
                <input className="form-control" style={{width:'130px',fontSize:'var(--fs-md)',padding:'3px 6px'}}
                  value={row.area_class} onChange={e=>update(i,'area_class',e.target.value)} placeholder="e.g. Grade C" list="class-list"/>
                <datalist id="class-list">{DEFAULT_AREA_CLASSES.map(c=><option key={c} value={c}/>)}</datalist>
              </td>
              <td><Num value={row.bioburden_limit} onChange={v=>update(i,'bioburden_limit',v)} placeholder="100" width="90px"/></td>
              <td>
                <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'3px 4px',width:'90px'}} value={row.bioburden_unit||'CFU/swab'} onChange={e=>update(i,'bioburden_unit',e.target.value)}>
                  {['CFU/swab','CFU/cm²','CFU/plate'].map(u=><option key={u}>{u}</option>)}
                </select>
              </td>
              <td style={{textAlign:'center'}}>
                <div style={{display:'flex',gap:'4px',justifyContent:'center'}}>
                  <button className="btn btn-outline btn-sm" style={{padding:'3px 8px',fontSize:'var(--fs-sm)'}} onClick={()=>saveRow(i)}><FaSave/></button>
                  <button className="btn btn-sm" title="Deactivate" style={{padding:'3px 8px',fontSize:'var(--fs-sm)',background:'none',color:'var(--danger-color)',border:'1px solid var(--danger-border)'}} onClick={()=>deleteRow(i)}><FaTrash/></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </FloatingScrollWrapper>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const AcceptanceCriteria = () => {
  const mobileImportInputRef = useRef(null);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('swab');
  const [solvents, setSolvents]       = useState([]);
  const [areaClasses, setAreaClasses] = useState([]);
  const [parts, setParts]             = useState([]);
  const [record, setRecord]           = useState(null);
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(false);
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [swabRows, setSwabRows]       = useState([]);
  const [rinseRows, setRinseRows]     = useState([]);
  // Global settings (saved with the record)
  const [swabArea, setSwabArea]       = useState(25);
  const [swabDiluent, setSwabDiluent] = useState(10);
  const [marPerCm2, setMarPerCm2]     = useState('');
  const [macoInfo, setMacoInfo]       = useState(null);
  const [macoScenarioPreview, setMacoScenarioPreview] = useState(null);
  const [fetchingMar, setFetchingMar] = useState(false);
  const [isDirty, setIsDirty]         = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [uploadingAmv, setUploadingAmv] = useState(null); // { type:'swab'|'rinse', idx }
  // MACO auto-fetch / contradiction alerting
  const [macoStale, setMacoStale]         = useState(null); // { fresh, freshInfo, saved } when saved MAR no longer matches current MACO calc
  const [macoUnavailable, setMacoUnavailable] = useState(false); // no MACO data found for any train on this line

  // Cleaning config rinsing agent map: { equipment_id: rinsing_agent }
  const [ccRinsingMap, setCcRinsingMap] = useState({});

  // Detergent criterion (driven by MACO settings)
  const [detergentEnabled,    setDetergentEnabled]    = useState(false);
  const [visualLabels, setVisualLabels] = useState({ pass: 'Pass', fail: 'Fail' });
  const [detergentMarPerCm2,  setDetergentMarPerCm2]  = useState('');

  // E-sign
  const [showESign, setShowESign]   = useState(false);
  const [eSignMode, setESignMode]   = useState('draft');
  const [eSignData, setESignData]   = useState({ password:'', comment:'', decision:'approve' });
  const [eSigning, setESigning]     = useState(false);
  const passwordRef = useRef(null);

  const activePlant = localStorage.getItem('activePlant') || '';
  const activeLine  = localStorage.getItem('activeLine')  || '';
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}'); } catch { return {}; } })();
  const role = String(user.role||'').trim().toLowerCase();
  const isSuperAdmin      = role === 'super admin';
  const isAdmin           = role === 'admin' || isSuperAdmin;
  const { canEarlier: canProductApprove, canFinal: canQaApprove, earlierLabel, finalLabel } = useApprovalRights('acceptance_criteria.approval');
  const status            = record?.status || '';
  const canEdit           = !status || status === 'Draft';
  const noLine            = !activeLine || activeLine === 'All' || activeLine === 'All Lines';
  const getSolventData    = (name) => solvents.find(s => s.solvent_name === name) || null;
  const getBioburden      = (cls)  => areaClasses.find(a => a.area_class === cls)  || null;

  // Derived swab limit (same for all parts)
  const swabApiLimit = calcSwabApiLimit(marPerCm2, swabArea, swabDiluent);

  // ── Load ───────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { plant_code: activePlant };
      const [solRes, clsRes, settRes] = await Promise.all([
        acceptanceCriteriaAPI.getSolvents(params),
        acceptanceCriteriaAPI.getAreaClasses(params),
        settingsAPI.getAll(),
      ]);
      setSolvents(solRes?.data?.data || []);
      setAreaClasses(clsRes?.data?.data || []);

      // Read detergent criterion flag from MACO settings
      const settings = settRes?.data?.data || {};
      try {
        const ce = JSON.parse(settings['maco_criteria_enabled'] || '{}');
        setDetergentEnabled(!!ce.detergent);
      } catch { setDetergentEnabled(false); }

      setVisualLabels({
        pass: settings['visual_limit_pass_label'] || 'Pass',
        fail: settings['visual_limit_fail_label'] || 'Fail'
      });

      // Build rinsing-agent map from Cleaning Config (equipment_id → rinsing_agent)
      let rinsingMap = {};
      try {
        const ccParams = { plant_code: activePlant };
        if (activeLine && activeLine !== 'All') ccParams.line_id = activeLine;
        const ccRes = await cleaningConfigAPI.getAll(ccParams);
        (ccRes?.data?.data || []).forEach(row => {
          if (row.equipment_id && row.rinsing_agent) {
            rinsingMap[row.equipment_id] = row.rinsing_agent;
          }
        });
      } catch { /* non-fatal */ }
      setCcRinsingMap(rinsingMap);

      if (!noLine) {
        const lp = { plant_code: activePlant, production_line: activeLine };
        const [partsRes, critRes] = await Promise.all([
          acceptanceCriteriaAPI.getParts(lp),
          acceptanceCriteriaAPI.getCriteria(lp),
        ]);
        const partList = partsRes?.data?.data || [];
        setParts(partList);
        const saved = critRes?.data?.data || null;
        setRecord(saved);
        if (saved) {
          setSwabArea(parseFloat(saved.swab_area_cm2) || 25);
          setSwabDiluent(parseFloat(saved.swab_diluent_ml) || 10);
          setMarPerCm2(saved.mar_per_cm2 != null ? String(saved.mar_per_cm2) : '');
          if (saved.detergent_mar_per_cm2 != null && parseFloat(saved.detergent_mar_per_cm2) > 0) {
            setDetergentMarPerCm2(String(saved.detergent_mar_per_cm2));
          }
        }

        // emptyRinseRow with rinsing agent pre-filled from cleaning config
        const emptyRinseRowWithAgent = (part) => ({
          ...emptyRinseRow(part),
          rinse_solvent: rinsingMap[part.equipment_id] || '',
        });

        const mergeRows = (savedArr, emptyFn) => {
          const savedMap = {};
          (savedArr||[]).forEach(r => { if (r.part_id) savedMap[r.part_id] = r; });
          const allIds = new Set([...partList.map(p=>p.part_id), ...(savedArr||[]).map(r=>r.part_id).filter(Boolean)]);
          return Array.from(allIds).map(id => {
            const part = partList.find(p => p.part_id === id);
            return savedMap[id] || (part ? emptyFn(part) : null);
          }).filter(Boolean);
        };
        setSwabRows(mergeRows(saved?.swab_criteria, emptySwabRow));
        setRinseRows(mergeRows(saved?.rinse_criteria, emptyRinseRowWithAgent));
        setIsDirty(false);
      }
    } catch { toast.error('Failed to load acceptance criteria data'); }
    finally  { setLoading(false); }
  }, [activePlant, activeLine, noLine]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (showESign) setTimeout(() => passwordRef.current?.focus(), 80); }, [showESign]);

  const updateSwab  = (idx,f,v) => { setSwabRows(prev=>{const n=[...prev];n[idx]={...n[idx],[f]:v};return n;}); setIsDirty(true); };
  const updateRinse = (idx,f,v) => { setRinseRows(prev=>{const n=[...prev];n[idx]={...n[idx],[f]:v};return n;}); setIsDirty(true); };
  const setGlobal   = (setter, val) => { setter(val); setIsDirty(true); };

  // ── AMV reference PDF attachments (swab/rinse) ──────────────────────────────
  const handleAmvUpload = async (type, idx, file) => {
    setUploadingAmv({ type, idx });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await analyticalMethodsAPI.uploadFile(fd);
      const uploaded = res?.data?.data;
      if (!uploaded) throw new Error('Upload failed');
      const entry = { filename: uploaded.filename, originalname: uploaded.originalname };
      if (type === 'swab') updateSwab(idx, 'amv_files', [...(swabRows[idx].amv_files||[]), entry]);
      else updateRinse(idx, 'amv_files', [...(rinseRows[idx].amv_files||[]), entry]);
    } catch { toast.error('PDF upload failed'); }
    finally { setUploadingAmv(null); }
  };
  const handleAmvRemove = (type, idx, filename) => {
    if (type === 'swab') updateSwab(idx, 'amv_files', (swabRows[idx].amv_files||[]).filter(f=>f.filename!==filename));
    else updateRinse(idx, 'amv_files', (rinseRows[idx].amv_files||[]).filter(f=>f.filename!==filename));
    analyticalMethodsAPI.deleteFile(filename).catch(()=>{});
  };

  // ── MACO governing MAR — compute (pure) ─────────────────────────────────────
  // Worst-case (lowest) MAR across every train on this line — merges backend
  // calc with the MACO Calculator's localStorage cache. Returns null if no
  // MACO data exists for any train on the line.
  const computeGoverningMar = useCallback(async () => {
    let bestMar = null, info = null, detergentMar = null;
    try {
      const res = await macoAPI.getGoverningMar({ production_line: activeLine });
      const d = res?.data?.data;
      if (d && d.mar_per_cm2 > 0) { bestMar = d.mar_per_cm2; info = d; detergentMar = d.detergent_mar_per_cm2 > 0 ? d.detergent_mar_per_cm2 : null; }
    } catch {}
    try {
      const savedMap = JSON.parse(localStorage.getItem('maco_governing_mar_map') || '{}');
      Object.values(savedMap).forEach(entry => {
        if (entry.mar_per_cm2 > 0) {
          if (bestMar === null || entry.mar_per_cm2 < bestMar) {
            bestMar = entry.mar_per_cm2;
            info = { ...info, ...entry, mar_per_cm2: entry.mar_per_cm2 };
            detergentMar = entry.detergentMar > 0 ? entry.detergentMar : null;
          }
        }
      });
    } catch {}
    if (bestMar === null || bestMar <= 0) return null;
    return { mar: bestMar, info, detergentMar };
  }, [activeLine]);

  // ── Fetch governing MAR from MACO (manual button — always applies) ─────────
  const fetchGoverningMar = async () => {
    if (noLine) return;
    setFetchingMar(true);
    try {
      const result = await computeGoverningMar();
      if (!result) {
        setMacoUnavailable(true);
        return toast.warn('No MACO data found for this line. Open MACO Calculator for this train first, then retry.');
      }
      setMacoUnavailable(false);
      setMacoStale(null);
      setMacoInfo(result.info);
      setMarPerCm2(String(parseFloat(result.mar.toFixed(6))));
      let detergentMsg = '';
      if (result.detergentMar > 0) {
        setDetergentMarPerCm2(String(parseFloat(result.detergentMar.toFixed(6))));
        detergentMsg = ` and detergent MAR ${parseFloat(result.detergentMar.toFixed(6))} µg/cm²`;
      }
      setIsDirty(true);
      const src = result.info?.api_name ? ` — ${result.info.api_name}` : '';
      toast.success(`Governing MAR loaded: ${parseFloat(result.mar.toFixed(6))} µg/cm²${detergentMsg}${src}`);
    } catch { toast.error('Failed to fetch MACO governing MAR'); }
    finally { setFetchingMar(false); }
  };

  const previewDraftMaco = async () => {
    if (noLine) return;
    setFetchingMar(true);
    try {
      const res = await macoAPI.previewGoverningMar({ production_line: activeLine, line_id: localStorage.getItem('activeLineId') || undefined });
      setMacoScenarioPreview(res?.data?.data || null);
      if (!res?.data?.data) toast.info('No MACO scenario data is available for this line yet.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to preview draft MACO impact');
    } finally { setFetchingMar(false); }
  };

  // ── MACO auto-fetch / contradiction check ───────────────────────────────────
  // If this line has no saved MAR yet, auto-fill it from MACO (allowAutoFill
  // only — never on a focus/visibility recheck, so it can't clobber an
  // in-progress edit). If a MAR is already saved, don't silently overwrite it
  // (it may be an approved value) — instead flag when the saved/displayed
  // value no longer matches what MACO would currently compute, or when no
  // MACO data exists at all for the trains on this line.
  const recheckMaco = useCallback(async (allowAutoFill) => {
    if (noLine) return;
    const result = await computeGoverningMar();
    const savedMar = record?.mar_per_cm2 != null ? parseFloat(record.mar_per_cm2) : null;
    if (!result) {
      setMacoUnavailable(!savedMar);
      setMacoStale(null);
      return;
    }
    setMacoUnavailable(false);
    if (savedMar === null) {
      if (allowAutoFill) {
        // Nothing saved yet for this line — auto-populate from MACO.
        setMacoInfo(result.info);
        setMarPerCm2(String(parseFloat(result.mar.toFixed(6))));
        setIsDirty(true);
      }
      setMacoStale(null);
    } else if (Math.abs(result.mar - savedMar) > Math.max(1e-9, savedMar * 1e-6)) {
      setMacoStale({ fresh: result.mar, freshInfo: result.info, saved: savedMar });
    } else {
      setMacoStale(null);
    }
  }, [noLine, record, computeGoverningMar]);

  // Runs once whenever the selected line's saved record (re)loads — the only
  // point allowed to auto-fill a blank MAR, since it means fresh data just
  // came in from the server rather than mid-session polling.
  useEffect(() => {
    if (noLine || loading) { setMacoStale(null); setMacoUnavailable(false); return; }
    let cancelled = false;
    (async () => { if (!cancelled) await recheckMaco(true); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noLine, loading, record]);

  // Re-check (banners only) whenever this tab regains focus — catches data
  // changed elsewhere (another tab, or navigating out to Product/Equipment
  // Master and back) without risking an overwrite of unsaved edits here.
  useEffect(() => {
    if (noLine) return;
    const onFocus = () => { if (document.visibilityState !== 'hidden') recheckMaco(false); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [noLine, recheckMaco]);

  const applyFreshMar = () => {
    if (!macoStale) return;
    setMarPerCm2(String(parseFloat(macoStale.fresh.toFixed(6))));
    setMacoInfo(macoStale.freshInfo);
    setIsDirty(true);
    setMacoStale(null);
    toast.success(`Governing MAR updated to ${parseFloat(macoStale.fresh.toFixed(6))} µg/cm²`);
  };

  // ── E-sign ─────────────────────────────────────────────────────────────────
  const openESign  = (mode) => {
    if (mode === 'returnToDraft' && status === 'Approved' && !pendingChangeRef) {
      setChangeRefGate(true);
      return;
    }
    setESignMode(mode); setESignData({password:'',comment:'',decision:'approve'}); setShowESign(true);
  };
  const closeESign = () => { setShowESign(false); setESigning(false); };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      setChangeRefGate(false);
      setESignMode('returnToDraft'); setESignData({password:'',comment:'',decision:'approve'}); setShowESign(true);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const handleConfirmESign = async () => {
    if (!eSignData.password) return toast.error('Password required');
    if (!eSignData.comment.trim()) return toast.error('Comment required');
    setESigning(true);
    try {
      if (eSignMode==='draft'||eSignMode==='submit') {
        const payload = {
          plant_code: activePlant, production_line: activeLine,
          swab_criteria: JSON.stringify(swabRows),
          rinse_criteria: JSON.stringify(rinseRows),
          swab_area_cm2: swabArea, swab_diluent_ml: swabDiluent,
          mar_per_cm2: marPerCm2 || 0,
          detergent_mar_per_cm2: detergentMarPerCm2 || 0,
          comment: eSignData.comment, password: eSignData.password,
          criteria_id: record?.id,
        };
        const res = await acceptanceCriteriaAPI.saveCriteria(payload);
        const saved = res?.data?.data;
        if (!saved) throw new Error('Save failed');
        setRecord(saved); setIsDirty(false);
        if (eSignMode==='submit') {
          const subRes = await acceptanceCriteriaAPI.submitCriteria(saved.id,{password:eSignData.password,comment:eSignData.comment});
          setRecord(subRes?.data?.data||saved);
          toast.success('Submitted for approval');
        } else { toast.success('Saved successfully'); }
      } else if (eSignMode==='returnToDraft') {
        const payload = {password:eSignData.password,comment:eSignData.comment};
        if (status === 'Approved' && pendingChangeRef) {
          payload.change_reference_type = pendingChangeRef.reference_type;
          payload.change_reference_number = pendingChangeRef.reference_number;
          payload.change_reference_files = pendingChangeRef.reference_files || [];
        }
        const res = await acceptanceCriteriaAPI.returnToDraft(record.id, payload);
        setRecord(res?.data?.data);
        setPendingChangeRef(null);
        toast.success('Returned to Draft — you can now edit');
      } else if (eSignMode==='delete') {
        await acceptanceCriteriaAPI.deleteCriteria(record.id,{password:eSignData.password,comment:eSignData.comment});
        setRecord(null); setSwabRows([]); setRinseRows([]);
        toast.success('Record deactivated');
      } else {
        const res = await acceptanceCriteriaAPI.approveCriteria(record.id,{
          stage:eSignMode, decision:eSignData.decision, comment:eSignData.comment, password:eSignData.password,
        });
        setRecord(res?.data?.data);
        const label = eSignData.decision==='approve'?'Approved':eSignData.decision==='return'?'Returned':'Rejected';
        toast.success(`${eSignMode==='product'?'Product':'QA'} review: ${label}`);
      }
      closeESign();
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.message||err.message||'Action failed');
    } finally { setESigning(false); }
  };

  // ── Export Excel ───────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const meta = [
      ['PrimeClean — Acceptance Criteria Export'],
      [`Unit/Plant: ${activePlant||'All'}   Section: ${activeLine||'All'}`],
      [`MAR: ${marPerCm2||'—'} µg/cm²   Swab Area: ${swabArea} cm²   Diluent: ${swabDiluent} mL`],
      [`Swab API Limit (same for all): ${swabApiLimit!==null?fmtLimit(swabApiLimit)+' µg/mL':'—'}`],
      [`Exported: ${new Date().toLocaleString()}   By: ${user.username||''}   Status: ${status||'Not Saved'}`],
      [],
    ];
    const swabHeader = ['#','Equipment','Part Name','Material','AMV Reference','Area Class','Bioburden Limit',
      `Chemical API Limit (µg/mL) [MAR=${marPerCm2||'?'} × ${swabArea}cm² ÷ ${swabDiluent}mL]`,'Remarks'];
    const swabData = swabRows.map((r,i) => {
      const bb = getBioburden(r.area_class);
      return [i+1,r.equipment_name,r.part_name,r.material,r.amv_reference||'',r.area_class,
        bb?`${bb.bioburden_limit} ${bb.bioburden_unit}`:'',
        swabApiLimit!==null?Number(fmtLimit(swabApiLimit)):'', r.remarks];
    });
    const wsSwab = XLSX.utils.aoa_to_sheet([...meta, swabHeader, ...swabData]);
    XLSX.utils.book_append_sheet(wb, wsSwab, 'Swab Criteria');

    const rinseHeader = ['#','Equipment','Part Name','Material','AMV Reference','Rinse Solvent','Rinse SA (cm²)','Rinse Vol (mL)',
      'API Limit (ppm)','TOC','pH Range','Conductivity','BET','Microbial','Notes','Remarks'];
    const rinseData = rinseRows.map((r,i) => {
      const lim = calcRinseLimit(r, marPerCm2);
      const sol = getSolventData(r.rinse_solvent);
      return [i+1,r.equipment_name,r.part_name,r.material,r.amv_reference||'',r.rinse_solvent,r.rinse_sa,r.rinse_vol,
        lim!==null?Number(fmtLimit(lim)):'',
        sol?`${sol.toc_limit} ${sol.toc_unit}`:'',
        sol?`${sol.ph_min}–${sol.ph_max}`:'',
        sol?`${sol.conductivity_limit} ${sol.conductivity_unit}`:'',
        sol?`${sol.bet_limit} ${sol.bet_unit}`:'',
        sol?`${sol.microbial_limit} ${sol.microbial_unit}`:'',
        r.rinse_notes, r.remarks];
    });
    const wsRinse = XLSX.utils.aoa_to_sheet([...meta, rinseHeader, ...rinseData]);
    XLSX.utils.book_append_sheet(wb, wsRinse, 'Rinse Criteria');
    XLSX.writeFile(wb, `AcceptanceCriteria_${activeLine.replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
    toast.success('Excel exported');
  };

  // ── Export PDF ─────────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = new jsPDF({ orientation:'landscape', unit:'pt', format:'a4' });
    const PW = doc.internal.pageSize.getWidth();
    const lm=30, rm=30;
    let y = 40;
    doc.setFontSize(14); doc.setFont('helvetica','bold');
    doc.text('Acceptance Criteria', lm, y); y+=18;
    doc.setFontSize(9); doc.setFont('helvetica','normal');
    doc.text(`Section: ${activeLine||'All'}   |   MAR: ${marPerCm2||'—'} µg/cm²   |   Swab Area: ${swabArea} cm²   |   Diluent: ${swabDiluent} mL`, lm, y); y+=13;
    doc.text(`Swab API Limit (all parts): ${swabApiLimit!==null?fmtLimit(swabApiLimit)+' µg/mL':'—'}   |   Status: ${status||'Not Saved'}`, lm, y); y+=13;
    doc.text(`Exported: ${new Date().toLocaleString()}   |   By: ${user.username||''}`, lm, y); y+=20;

    const drawTable = (title, headers, rows) => {
      if (y > doc.internal.pageSize.getHeight()-60) { doc.addPage(); y=40; }
      doc.setFontSize(11); doc.setFont('helvetica','bold');
      doc.text(title, lm, y); y+=14;
      const usableW = PW-lm-rm;
      const colW = Math.min(usableW/headers.length, 120);
      const rowH=16, cellPad=4;
      doc.setFontSize(7.5); doc.setFont('helvetica','bold');
      doc.setFillColor(230,235,245);
      doc.rect(lm, y, usableW, rowH, 'F');
      headers.forEach((h,i) => {
        doc.setDrawColor(180,185,200);
        doc.rect(lm+i*colW, y, colW, rowH);
        doc.text(String(h).slice(0,18), lm+i*colW+cellPad, y+rowH-5);
      });
      y += rowH;
      doc.setFont('helvetica','normal');
      rows.forEach((row, ri) => {
        if (y > doc.internal.pageSize.getHeight()-40) { doc.addPage(); y=40; }
        if (ri%2===0) { doc.setFillColor(248,250,252); doc.rect(lm,y,usableW,rowH,'F'); }
        row.forEach((cell,i) => {
          doc.setDrawColor(210,215,225);
          doc.rect(lm+i*colW, y, colW, rowH);
          doc.text(String(cell??'').slice(0,20), lm+i*colW+cellPad, y+rowH-5);
        });
        y += rowH;
      });
      y += 14;
    };

    drawTable(
      `Swab Criteria — API Limit = ${swabApiLimit!==null?fmtLimit(swabApiLimit)+' µg/mL (all parts)':'Enter MAR'}`,
      ['#','Equipment','Part','Material','AMV Ref.','Area Class','Bioburden Limit'],
      swabRows.map((r,i)=>{
        const bb=getBioburden(r.area_class);
        return [i+1,r.equipment_name,r.part_name,r.material,r.amv_reference||'—',r.area_class||'—',bb?`${bb.bioburden_limit} ${bb.bioburden_unit}`:'—'];
      })
    );

    drawTable(
      'Rinse Criteria',
      ['#','Equipment','Part','AMV Ref.','Solvent','Rinse SA','Rinse Vol','API Limit (ppm)','TOC','pH','Conductivity'],
      rinseRows.map((r,i)=>{
        const lim=calcRinseLimit(r,marPerCm2);
        const sol=getSolventData(r.rinse_solvent);
        return [i+1,r.equipment_name,r.part_name,r.amv_reference||'—',r.rinse_solvent||'—',r.rinse_sa||'',r.rinse_vol||'',
          lim!==null?fmtLimit(lim):'—',
          sol?`${sol.toc_limit} ${sol.toc_unit}`:'—',
          sol?`${sol.ph_min}–${sol.ph_max}`:'—',
          sol?`${sol.conductivity_limit} ${sol.conductivity_unit}`:'—'];
      })
    );
    stampPdfFooter(doc, 'acceptance_criteria');
    doc.save(`AcceptanceCriteria_${activeLine.replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success('PDF exported');
  };

  // ── Import Template ────────────────────────────────────────────────────────
  const handleDownloadTemplate = (type) => {
    const wb = XLSX.utils.book_new();
    if (type==='swab') {
      const ws = XLSX.utils.aoa_to_sheet([
        ['Equipment Name','Part Name','Material','Area Class','Remarks'],
        ...swabRows.map(r=>[r.equipment_name,r.part_name,r.material,'','']),
      ]);
      XLSX.utils.book_append_sheet(wb, ws, 'Swab Template');
    } else {
      const ws = XLSX.utils.aoa_to_sheet([
        ['Equipment Name','Part Name','Material','Rinsing Solvent','Rinse SA (cm²)','Rinse Volume (mL)','Notes','Remarks'],
        ...rinseRows.map(r=>[r.equipment_name,r.part_name,r.material,'',r.surface_area_ref||'','','','']),
      ]);
      XLSX.utils.book_append_sheet(wb, ws, 'Rinse Template');
    }
    XLSX.writeFile(wb, `Template_${type}_AcceptanceCriteria.xlsx`);
    toast.success('Template downloaded');
  };

  // ── Import ─────────────────────────────────────────────────────────────────
  const handleImportFile = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, {type:'binary'});
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {defval:''});
        if (!data.length) return toast.error('No data found in file');
        const match = (row, d) =>
          String(d['Equipment Name']||'').trim().toLowerCase()===String(row.equipment_name||'').trim().toLowerCase() &&
          String(d['Part Name']||'').trim().toLowerCase()===String(row.part_name||'').trim().toLowerCase();
        if (type==='swab') {
          setSwabRows(prev=>prev.map(row=>{
            const d=data.find(x=>match(row,x)); if(!d) return row;
            return {...row, area_class:d['Area Class']||row.area_class, remarks:d['Remarks']||row.remarks};
          }));
          setIsDirty(true); toast.success(`Swab data imported — ${data.length} row(s)`);
        } else {
          setRinseRows(prev=>prev.map(row=>{
            const d=data.find(x=>match(row,x)); if(!d) return row;
            return {...row,
              rinse_solvent: d['Rinsing Solvent']||row.rinse_solvent,
              rinse_sa:      d['Rinse SA (cm²)']!==''?String(d['Rinse SA (cm²)']):row.rinse_sa,
              rinse_vol:     d['Rinse Volume (mL)']!==''?String(d['Rinse Volume (mL)']):row.rinse_vol,
              rinse_notes:   d['Notes']||row.rinse_notes,
              remarks:       d['Remarks']||row.remarks,
            };
          }));
          setIsDirty(true); toast.success(`Rinse data imported — ${data.length} row(s)`);
        }
      } catch { toast.error('Failed to parse import file'); }
    };
    reader.readAsBinaryString(file);
    e.target.value='';
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <div className="loading-container"><div className="spinner"/></div>;

  const calcCell = { background:'color-mix(in srgb, #7c3aed 10%, var(--card-bg))', fontWeight:700, fontSize:'var(--fs-md)', whiteSpace:'nowrap' };

  // Detergent limit helpers
  const detSwabLimit = (() => {
    const m = parseFloat(detergentMarPerCm2), a = parseFloat(swabArea), v = parseFloat(swabDiluent);
    return m && a && v ? m * a / v : null;
  })();
  const calcDetRinseLimit = (row) => {
    const m = parseFloat(detergentMarPerCm2), sa = parseFloat(row.rinse_sa), v = parseFloat(row.rinse_vol);
    return m && sa && v ? m * sa / v : null;
  };

  const eSignTitle = {
    draft:          'Save — Electronic Signature',
    submit:         'Submit for Approval — Electronic Signature',
    product:        `${earlierLabel} — Electronic Signature`,
    qa:             `${finalLabel} — Electronic Signature`,
    returnToDraft:  'Return to Draft — Electronic Signature',
    delete:         'Deactivate Record — Electronic Signature',
  }[eSignMode];

  const ActionBar = () => (
    <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',flexWrap:'wrap',gap:'8px',padding:'12px 0 0 0'}}>
      {isDirty&&<span style={{fontSize:'var(--fs-md)',color:'var(--warning-color)'}}>● Unsaved changes</span>}
      {canEdit&&!noLine&&<button className="btn btn-primary btn-sm" onClick={()=>openESign('draft')} disabled={!isDirty}><FaSave style={{marginRight:'4px'}}/>Save</button>}
      {canEdit&&record&&!noLine&&<button className="btn btn-outline btn-sm" onClick={()=>openESign('submit')}><FaFileUpload style={{marginRight:'4px'}}/>Submit for Approval</button>}
      {status==='Pending Approval'    &&canProductApprove&&<button className="btn btn-success btn-sm" onClick={()=>openESign('product')}><FaCheck style={{marginRight:'4px'}}/>{earlierLabel}</button>}
      {status==='Pending QA Approval' &&canQaApprove     &&<button className="btn btn-success btn-sm" onClick={()=>openESign('qa')}><FaCheck style={{marginRight:'4px'}}/>{finalLabel}</button>}
    </div>
  );

  return (
    <div>
      <div>
        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'10px',marginBottom:'20px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
            <div>
              <h2 className="page-title">Acceptance Criteria</h2>
              <p className="page-subtitle">Swab and rinse acceptance limits derived from governing MAR/MACO, per equipment part.</p>
            </div>
            {record&&status&&status!=='Draft'&&<StatusBadge status={status}/>}
          </div>
          <div className="toolbar-full" style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
            {record&&!noLine&&isAdmin&&status&&status!=='Draft'&&(
              <button className="btn btn-outline btn-sm" onClick={()=>openESign('returnToDraft')} title="Return to Draft for editing">
                <FaEdit style={{marginRight:'4px'}}/>Edit
              </button>
            )}
            {record&&!noLine&&isAdmin&&(
              <button className="btn btn-sm" style={{background:'none',border:'1px solid var(--danger-border)',color:'var(--danger-color)'}} onClick={()=>openESign('delete')} title="Deactivate record">
                <FaTrashAlt style={{marginRight:'4px'}}/>Deactivate
              </button>
            )}
            {activeTab!=='setup'&&!noLine&&(
              <>
                <button className="btn btn-outline btn-sm" onClick={()=>handleDownloadTemplate(activeTab==='swab'?'swab':'rinse')} title="Download import template"><FaDownload style={{marginRight:'4px'}}/>Import Template</button>
                <label className="btn btn-info btn-sm" style={{cursor:'pointer',margin:0}} title="Import from Excel">
                  <FaUpload style={{marginRight:'4px'}}/>Import
                  <input type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={e=>handleImportFile(e,activeTab==='swab'?'swab':'rinse')}/>
                </label>
              </>
            )}
            {!noLine&&<button className="btn btn-success btn-sm" onClick={handleExportExcel}><FaFileExcel style={{marginRight:'4px'}}/>Excel</button>}
            {!noLine&&<button className="btn btn-danger btn-sm" onClick={handleExportPdf}><FaFilePdf style={{marginRight:'4px'}}/>PDF</button>}
          </div>

          <div className="toolbar-compact" style={{gap:'10px'}}>
            {!noLine&&<button className="btn btn-success btn-sm" onClick={handleExportExcel}><FaFileExcel style={{marginRight:'4px'}}/>Excel</button>}
            <ActionsMenu
              title="More actions"
              items={[
                { label: 'Edit', icon: <FaEdit />, show: !!(record&&!noLine&&isAdmin&&status&&status!=='Draft'), onClick: ()=>openESign('returnToDraft') },
                { label: 'Version History', icon: <FaClipboardCheck />, show: !!(record&&!noLine), onClick: ()=>setHistoryModalOpen(true) },
                { label: 'Deactivate', icon: <FaTrashAlt />, danger: true, show: !!(record&&!noLine&&isAdmin), onClick: ()=>openESign('delete') },
                { label: 'Import Template', icon: <FaDownload />, show: activeTab!=='setup'&&!noLine, onClick: ()=>handleDownloadTemplate(activeTab==='swab'?'swab':'rinse') },
                { label: 'Import', icon: <FaUpload />, show: activeTab!=='setup'&&!noLine, onClick: () => mobileImportInputRef.current?.click() },
                { divider: true },
                { label: 'Export PDF', icon: <FaFilePdf />, show: !noLine, onClick: handleExportPdf },
              ]}
            />
            <input ref={mobileImportInputRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={e=>handleImportFile(e,activeTab==='swab'?'swab':'rinse')}/>
          </div>
        </div>

        {/* Context bar */}
        <div style={{padding:'8px 20px',borderBottom:'1px solid var(--border-color)',display:'flex',gap:'16px',flexWrap:'wrap',alignItems:'center',fontSize:'var(--fs-md)'}}>
          <span><strong>Unit / Plant:</strong> {activePlant||'All'}</span>
          <span><strong>Section:</strong> {activeLine||'All'}</span>
          {noLine&&<span style={{color:'var(--warning-text)',background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:'6px',padding:'2px 10px'}}>
            <FaInfoCircle style={{marginRight:'4px'}}/>Select a specific Section to load equipment parts.
          </span>}
        </div>

        {/* Approval trail */}
        {record&&(
          <div style={{padding:'7px 20px',borderBottom:'1px solid var(--border-color)',display:'flex',gap:'20px',flexWrap:'wrap',fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>
            {record.saved_by_name&&<span><strong>Saved:</strong> {record.saved_by_name} on {new Date(record.saved_at).toLocaleString()}</span>}
            {record.product_approved_by_name&&<span><strong>Product:</strong> {record.product_approval_decision} by {record.product_approved_by_name} on {new Date(record.product_approved_at).toLocaleString()}</span>}
            {record.qa_approved_by_name     &&<span><strong>QA:</strong> {record.qa_approval_decision} by {record.qa_approved_by_name} on {new Date(record.qa_approved_at).toLocaleString()}</span>}
          </div>
        )}

        <div className="stat-card-grid" style={{padding:'12px 20px 0'}}>
          <StatCard compact filterHint title="Swab Criteria" value={swabRows.length} icon={FaVial} color="primary" active={activeTab==='swab'} onClick={()=>setActiveTab('swab')} />
          <StatCard compact filterHint title="Rinse Criteria" value={rinseRows.length} icon={FaTint} color="info" active={activeTab==='rinse'} onClick={()=>setActiveTab('rinse')} />
          <StatCard compact filterHint title="Master Limits Setup" value={solvents.length + areaClasses.length} icon={FaCog} color="warning" active={activeTab==='setup'} onClick={()=>setActiveTab('setup')} />
          <StatCard compact title="Approval Status" value={record?.status || 'Draft'} icon={FaClipboardCheck} color={record?.status==='Approved'?'success':'info'} />
        </div>

        {/* Tabs */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid var(--border-color)',padding:'0 20px',background:'var(--bg-color)',gap:'20px',overflowX:'auto'}}>
          <div style={{display:'flex',gap:'20px'}}>
            {[{k:'swab',l:'Swab Criteria'},{k:'rinse',l:'Rinse Criteria'},{k:'setup',l:'Setup — Master Limits'}].map(t=>(
              <button key={t.k} onClick={()=>setActiveTab(t.k)} style={{
                padding:'12px 4px',border:'none',background:'none',cursor:'pointer',
                fontWeight:activeTab===t.k?600:400,
                borderBottom:activeTab===t.k?'2px solid var(--success-color)':'2px solid transparent',
                color:activeTab===t.k?'var(--success-color)':'var(--text-secondary)',
                fontSize:'var(--fs-lg)',whiteSpace:'nowrap'
              }}>{t.l}</button>
            ))}
          </div>
        </div>

        <div style={{padding:'20px'}}>

          {/* ══ SETUP TAB ══════════════════════════════════════════════════ */}
          {activeTab==='setup'&&(
            <>
              {/* Global settings block */}
              <div style={{padding:'16px 20px',marginBottom:'24px',background:'color-mix(in srgb, #2563eb 4%, var(--bg-color))',borderRadius:'8px',border:'1px solid color-mix(in srgb, #2563eb 15%, var(--border-color))'}}>
                <div style={{fontWeight:700,fontSize:'var(--fs-base)',marginBottom:'14px'}}>Sampling Settings — Applied to All Parts</div>
                <div style={{display:'flex',gap:'32px',flexWrap:'wrap',alignItems:'flex-start'}}>
                  {/* MAR/cm² */}
                  <div>
                    <div style={{fontSize:'var(--fs-md)',fontWeight:600,marginBottom:'6px',color:'var(--text-secondary)'}}>Governing MAR (µg/cm²)</div>
                    <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                      <input type="number" min="0" step="0.000001" className="form-control"
                        style={{width:'130px',fontSize:'var(--fs-base)',fontWeight:700,padding:'5px 8px'}}
                        value={marPerCm2} disabled={!canEdit}
                        onChange={e=>{setMarPerCm2(e.target.value);setIsDirty(true);}}
                        placeholder="e.g. 0.000250"/>
                      <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>µg/cm²</span>
                      {!noLine&&(
                        <>
                          <button className="btn btn-outline btn-sm" onClick={fetchGoverningMar} disabled={fetchingMar}
                            title="Apply the approved governing MAR from MACO" style={{display:'flex',alignItems:'center',gap:'4px'}}>
                            <FaSync style={{fontSize:'var(--fs-xs)'}} className={fetchingMar?'fa-spin':''}/>
                            {fetchingMar?'Fetching…':'Fetch approved MAR'}
                          </button>
                          <button className="btn btn-outline btn-sm" onClick={previewDraftMaco} disabled={fetchingMar}
                            title="Compare draft and approved master data without changing Acceptance Criteria" style={{display:'flex',alignItems:'center',gap:'4px'}}>
                            Preview draft impact
                          </button>
                        </>
                      )}
                    </div>
                    {macoInfo&&(
                      <div style={{fontSize:'var(--fs-xs)',color:'var(--success-color)',marginTop:'4px'}}>
                        {macoInfo.api_name} | {macoInfo.calculation_basis} | MACO: {parseFloat(macoInfo.calculated_maco_mg).toFixed(4)} mg
                      </div>
                    )}
                    {macoScenarioPreview&&(
                      <div style={{marginTop:'8px',padding:'8px 10px',borderRadius:'6px',background:'var(--warning-bg)',border:'1px solid var(--warning-border)',fontSize:'var(--fs-sm)'}}>
                        <strong>Draft scenario preview only</strong> · MAR {Number(macoScenarioPreview.mar_per_cm2 || 0).toPrecision(5)} µg/cm²
                        {macoScenarioPreview.provisional_delta_percent != null && <> · {macoScenarioPreview.provisional_delta_percent < 0 ? 'stricter' : macoScenarioPreview.provisional_delta_percent > 0 ? 'looser' : 'unchanged'} by {Math.abs(macoScenarioPreview.provisional_delta_percent).toFixed(1)}% vs approved baseline</>}
                        <div>Source: {macoScenarioPreview.api_name} ({macoScenarioPreview.api_status}) → {macoScenarioPreview.product_name} ({macoScenarioPreview.product_status}). Not applied to saved criteria.</div>
                        <div>Projected swab API concentration: {swabArea && swabDiluent ? (Number(macoScenarioPreview.mar_per_cm2) * Number(swabArea) / Number(swabDiluent)).toPrecision(5) : '—'} µg/mL.</div>
                        {rinseRows.slice(0, 3).map((row, i) => <div key={`scenario-rinse-${i}`}>Projected rinse API concentration · {row.part_name || row.equipment_name}: {calcRinseLimit(row, macoScenarioPreview.mar_per_cm2) ?? '—'} ppm</div>)}
                      </div>
                    )}
                    <div style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)',marginTop:'2px'}}>
                      Same for all swab and rinse — from governing MACO criteria
                    </div>
                    {macoStale&&(
                      <div style={{marginTop:'6px',padding:'6px 8px',borderRadius:'6px',background:'var(--warning-bg)',border:'1px solid var(--warning-border)',fontSize:'var(--fs-sm)',color:'var(--warning-text)',display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                        <FaInfoCircle style={{flexShrink:0}}/>
                        <span>Saved MAR ({macoStale.saved}) no longer matches current MACO calc ({parseFloat(macoStale.fresh.toFixed(6))}{macoStale.freshInfo?.api_name?` — ${macoStale.freshInfo.api_name}`:''}).</span>
                        {canEdit&&<button className="btn btn-outline btn-sm" style={{padding:'2px 8px',fontSize:'var(--fs-sm)'}} onClick={applyFreshMar}>Update to {parseFloat(macoStale.fresh.toFixed(6))}</button>}
                      </div>
                    )}
                    {macoUnavailable&&!macoStale&&(
                      <div style={{marginTop:'6px',padding:'6px 8px',borderRadius:'6px',background:'var(--danger-bg)',border:'1px solid var(--danger-border)',fontSize:'var(--fs-sm)',color:'var(--danger-text)',display:'flex',alignItems:'center',gap:'8px'}}>
                        <FaInfoCircle style={{flexShrink:0}}/>
                        <span>No MACO data found for trains on this line. Run the MACO Calculator for this train first.</span>
                      </div>
                    )}
                  </div>

                  {/* Swab Area */}
                  <div>
                    <div style={{fontSize:'var(--fs-md)',fontWeight:600,marginBottom:'6px',color:'var(--text-secondary)'}}>Swab Area</div>
                    <div style={{display:'flex',gap:'12px'}}>
                      {SWAB_AREA_OPTIONS.map(v=>(
                        <label key={v} style={{display:'flex',alignItems:'center',gap:'4px',cursor:canEdit?'pointer':'default',fontSize:'var(--fs-base)'}}>
                          <input type="radio" name="swabArea" value={v} checked={Number(swabArea)===v}
                            disabled={!canEdit} onChange={()=>setGlobal(setSwabArea,v)}/>
                          <strong>{v} cm²</strong>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Diluent */}
                  <div>
                    <div style={{fontSize:'var(--fs-md)',fontWeight:600,marginBottom:'6px',color:'var(--text-secondary)'}}>Diluent Volume</div>
                    <div style={{display:'flex',gap:'12px'}}>
                      {SWAB_DILUENT_OPTIONS.map(v=>(
                        <label key={v} style={{display:'flex',alignItems:'center',gap:'4px',cursor:canEdit?'pointer':'default',fontSize:'var(--fs-base)'}}>
                          <input type="radio" name="swabDiluent" value={v} checked={Number(swabDiluent)===v}
                            disabled={!canEdit} onChange={()=>setGlobal(setSwabDiluent,v)}/>
                          <strong>{v} mL</strong>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Derived swab limit */}
                  <div style={{padding:'10px 16px',background:'color-mix(in srgb, #7c3aed 8%, var(--bg-color))',borderRadius:'8px',border:'1px solid color-mix(in srgb, #7c3aed 20%, var(--border-color))'}}>
                    <div style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)',marginBottom:'4px'}}>Swab API Limit (all parts)</div>
                    <div style={{fontSize:'var(--fs-xl)',fontWeight:700,color:'#7c3aed'}}>
                      {swabApiLimit!==null ? `${fmtLimit(swabApiLimit)} ppm (µg/mL)` : '—'}
                    </div>
                    <div style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)',marginTop:'2px'}}>
                      = {marPerCm2||'MAR'} × {swabArea} ÷ {swabDiluent}
                    </div>
                  </div>

                  {/* Detergent MAR — shown when detergent criterion is ON in MACO */}
                  {detergentEnabled && !detergentMarPerCm2 && !canEdit && (
                    <div style={{padding:'8px 14px',background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:'6px',fontSize:'var(--fs-sm)',color:'var(--warning-text)'}}>
                      <strong>Detergent limit not set.</strong> Use the <strong>Edit</strong> button (top-right) to return this record to Draft, then enter the Detergent MAR in Setup.
                    </div>
                  )}
                  {detergentEnabled && (
                    <div style={{padding:'10px 16px',background:'color-mix(in srgb, #f59e0b 8%, var(--bg-color))',borderRadius:'8px',border:'2px solid #f59e0b'}}>
                      <div style={{fontSize:'var(--fs-sm)',fontWeight:700,color:'#b45309',marginBottom:'6px'}}>
                        Detergent MAR (µg/cm²)
                        <span style={{fontWeight:400,marginLeft:'6px',fontSize:'var(--fs-xs)'}}>Governed by MACO Detergent Criterion</span>
                      </div>
                      <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                        <input type="number" min="0" step="0.000001" className="form-control"
                          style={{width:'130px',fontSize:'var(--fs-base)',fontWeight:700,padding:'5px 8px',borderColor:'#f59e0b'}}
                          value={detergentMarPerCm2} disabled={!canEdit}
                          onChange={e=>{setDetergentMarPerCm2(e.target.value);setIsDirty(true);}}
                          placeholder="e.g. 0.000100"/>
                        <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>µg/cm²</span>
                      </div>
                      <div style={{fontSize:'var(--fs-xs)',color:'#b45309',marginTop:'4px'}}>
                        Swab Detergent Limit: {detSwabLimit!==null?`${fmtLimit(detSwabLimit)} µg/mL`:'—'}
                        {' '}= {detergentMarPerCm2||'Det.MAR'} × {swabArea} ÷ {swabDiluent}
                      </div>
                    </div>
                  )}
                </div>
                {canEdit&&!noLine&&isDirty&&(
                  <div style={{marginTop:'12px',paddingTop:'12px',borderTop:'1px solid color-mix(in srgb, #2563eb 15%, var(--border-color))'}}>
                    <button className="btn btn-primary btn-sm" onClick={()=>openESign('draft')}>
                      <FaSave style={{marginRight:'4px'}}/>Save Settings
                    </button>
                    <span style={{fontSize:'var(--fs-sm)',color:'var(--warning-color)',marginLeft:'10px'}}>● Unsaved changes</span>
                  </div>
                )}
              </div>

              <SolventTable solvents={solvents} setSolvents={setSolvents} plantCode={activePlant}/>
              <AreaClassTable areaClasses={areaClasses} setAreaClasses={setAreaClasses} plantCode={activePlant}/>
            </>
          )}

          {/* ══ SWAB TAB ═══════════════════════════════════════════════════ */}
          {activeTab==='swab'&&(
            <>
              {noLine ? (
                <div style={{textAlign:'center',color:'var(--text-secondary)',padding:'60px 0'}}>
                  Select a Section from the header to load equipment parts.
                </div>
              ) : parts.length===0 ? (
                <div style={{textAlign:'center',color:'var(--text-secondary)',padding:'60px 0'}}>
                  No equipment parts found for this section. Add parts via Equipment Master.
                </div>
              ) : (
                <>
                  {/* Swab limit summary bar */}
                  <div style={{display:'flex',alignItems:'center',gap:'20px',flexWrap:'wrap',padding:'10px 14px',marginBottom:'14px',background:'color-mix(in srgb, #7c3aed 5%, var(--bg-color))',borderRadius:'8px',border:'1px solid color-mix(in srgb, #7c3aed 15%, var(--border-color))'}}>
                    <div>
                      <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>Governing MAR</span>
                      <span style={{marginLeft:'6px',fontWeight:700,color:'#7c3aed',fontSize:'var(--fs-lg)'}}>
                        {marPerCm2?`${marPerCm2} µg/cm²`:<span style={{color:'var(--warning-text)'}}>Not set — go to Setup</span>}
                      </span>
                    </div>
                    <div style={{width:'1px',height:'28px',background:'var(--border-color)'}}/>
                    <div>
                      <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>Swab Area</span>
                      <span style={{marginLeft:'6px',fontWeight:700,fontSize:'var(--fs-base)'}}>{swabArea} cm²</span>
                    </div>
                    <div>
                      <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>Diluent</span>
                      <span style={{marginLeft:'6px',fontWeight:700,fontSize:'var(--fs-base)'}}>{swabDiluent} mL</span>
                    </div>
                    <div style={{width:'1px',height:'28px',background:'var(--border-color)'}}/>
                    <div style={{padding:'6px 14px',background:'color-mix(in srgb, #7c3aed 12%, var(--bg-color))',borderRadius:'6px'}}>
                      <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>Chemical API Limit (all parts) </span>
                      <span style={{fontWeight:800,color:'#7c3aed',fontSize:'var(--fs-lg)'}}>
                        {swabApiLimit!==null?`${fmtLimit(swabApiLimit)} ppm (µg/mL)`:'—'}
                      </span>
                    </div>
                    <span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>Only microbial (bioburden) varies per part</span>
                  </div>

                  <FloatingScrollWrapper>
                    <table style={{minWidth: detergentEnabled ? '1120px' : '780px'}}>
                      <thead>
                        <tr>
                          <th style={{minWidth:'36px',textAlign:'center',padding:'5px 6px',fontSize:'var(--fs-sm)'}}>#</th>
                          <th style={{minWidth:'160px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Equipment</th>
                          <th style={{minWidth:'140px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Part Name</th>
                          <th style={{minWidth:'110px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Material</th>
                          <th rowSpan={2} style={{minWidth:'160px',padding:'5px 8px',fontSize:'var(--fs-sm)',verticalAlign:'bottom'}} title="Manual reference + PDF for now — will link to LIMS">AMV Reference</th>
                          <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #16a34a 8%, var(--bg-color))',borderBottom:'none',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Microbial — Bioburden</th>
                          {detergentEnabled && (
                            <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #f59e0b 10%, var(--bg-color))',borderBottom:'none',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>
                              Detergent (Chemical)
                            </th>
                          )}
                          <th style={{minWidth:'240px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Remarks</th>
                        </tr>
                        <tr>
                          <th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/>
                          <th style={{minWidth:'120px',background:'color-mix(in srgb, #16a34a 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Area Class</th>
                          <th style={{minWidth:'130px',background:'color-mix(in srgb, #16a34a 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Bioburden Limit</th>
                          {detergentEnabled && <>
                            <th style={{minWidth:'150px',background:'color-mix(in srgb, #f59e0b 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>
                              Limit (µg/mL)
                              <div style={{fontSize:'var(--fs-xs)',fontWeight:400,color:'#b45309'}}>= Det.MAR × {swabArea} ÷ {swabDiluent}</div>
                            </th>
                            <th style={{minWidth:'120px',background:'color-mix(in srgb, #f59e0b 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Visual Limit</th>
                          </>}
                          <th style={{padding:'3px 6px'}}/>
                        </tr>
                      </thead>
                      <tbody>
                        {swabRows.map((row,idx)=>{
                          const bb = getBioburden(row.area_class);
                          return (
                            <tr key={row.part_id||idx} style={{height:'34px'}}>
                              <td style={{textAlign:'center',color:'var(--text-secondary)',fontSize:'var(--fs-sm)',padding:'3px 6px'}}>{idx+1}</td>
                              <td style={{padding:'3px 8px'}}><span style={{fontSize:'var(--fs-sm)',fontWeight:600}}>{row.equipment_name}</span></td>
                              <td style={{padding:'3px 8px'}}><span style={{fontSize:'var(--fs-sm)'}}>{row.part_name}</span></td>
                              <td style={{padding:'3px 8px'}}><span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>{row.material}</span></td>
                              <td style={{padding:'3px 6px'}}>
                                <AmvCell reference={row.amv_reference} files={row.amv_files||[]} disabled={!canEdit}
                                  uploading={uploadingAmv?.type==='swab' && uploadingAmv?.idx===idx}
                                  onReference={v=>updateSwab(idx,'amv_reference',v)}
                                  onUpload={f=>handleAmvUpload('swab',idx,f)}
                                  onRemove={fn=>handleAmvRemove('swab',idx,fn)}/>
                              </td>
                              <td style={{background:'color-mix(in srgb, #16a34a 3%, var(--card-bg))',padding:'3px 6px'}}>
                                {canEdit
                                  ? <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'2px 4px',width:'110px'}}
                                      value={row.area_class} onChange={e=>updateSwab(idx,'area_class',e.target.value)}>
                                      <option value="">— Select —</option>
                                      {areaClasses.map(ac=><option key={ac.id} value={ac.area_class}>{ac.area_class}</option>)}
                                    </select>
                                  : <span style={{fontSize:'var(--fs-sm)'}}>{row.area_class||'—'}</span>}
                              </td>
                              <td style={{background:'color-mix(in srgb, #16a34a 5%, var(--card-bg))',padding:'3px 8px'}}>
                                {bb
                                  ? <span style={{fontSize:'var(--fs-sm)',fontWeight:600,color:'#15803d'}}>{bb.bioburden_limit} {bb.bioburden_unit}</span>
                                  : <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>{row.area_class?'Not in master':'—'}</span>}
                              </td>
                              {detergentEnabled && <>
                                <td style={{background:'color-mix(in srgb, #f59e0b 6%, var(--card-bg))',padding:'3px 8px'}}>
                                  {detSwabLimit !== null
                                    ? <span style={{fontSize:'var(--fs-sm)',fontWeight:700,color:'#b45309'}}>{fmtLimit(detSwabLimit)} µg/mL</span>
                                    : <span style={{fontSize:'var(--fs-sm)',color:'#b45309'}}>Set Det. MAR in Setup ↑</span>}
                                </td>
                                <td style={{background:'color-mix(in srgb, #f59e0b 4%, var(--card-bg))',padding:'3px 8px'}}>
                                  {canEdit
                                    ? <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'2px 4px',width:'90px'}}
                                        value={row.visual_limit || 'Pass'} onChange={e=>updateSwab(idx,'visual_limit',e.target.value)}>
                                        <option value="Pass">{visualLabels.pass}</option>
                                        <option value="Fail">{visualLabels.fail}</option>
                                      </select>
                                    : <span style={{fontSize:'var(--fs-sm)',fontWeight:600,color:row.visual_limit==='Fail'?'var(--danger-color)':'var(--success-color)'}}>{row.visual_limit==='Fail' ? visualLabels.fail : visualLabels.pass}</span>}
                                </td>
                              </>}
                              <td style={{padding:'3px 6px'}}>
                                <textarea className="form-control" rows={1} style={{width:'230px',fontSize:'var(--fs-sm)',padding:'2px 6px',resize:'vertical'}}
                                  value={row.remarks} disabled={!canEdit}
                                  onChange={e=>updateSwab(idx,'remarks',e.target.value)} placeholder="Remarks…"/>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </FloatingScrollWrapper>
                  <ActionBar/>
                </>
              )}
            </>
          )}

          {/* ══ RINSE TAB ══════════════════════════════════════════════════ */}
          {activeTab==='rinse'&&(
            <>
              {noLine ? (
                <div style={{textAlign:'center',color:'var(--text-secondary)',padding:'60px 0'}}>
                  Select a Section from the header to load equipment parts.
                </div>
              ) : parts.length===0 ? (
                <div style={{textAlign:'center',color:'var(--text-secondary)',padding:'60px 0'}}>
                  No equipment parts found for this section.
                </div>
              ) : (
                <>
                  {/* Rinse MAR summary bar */}
                  <div style={{display:'flex',alignItems:'center',gap:'16px',flexWrap:'wrap',padding:'8px 14px',marginBottom:'12px',background:'color-mix(in srgb, #2563eb 4%, var(--bg-color))',borderRadius:'8px',border:'1px solid color-mix(in srgb, #2563eb 15%, var(--border-color))'}}>
                    <div>
                      <span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>Governing MAR</span>
                      <span style={{marginLeft:'6px',fontWeight:700,color:'#2563eb',fontSize:'var(--fs-lg)'}}>
                        {marPerCm2?`${marPerCm2} µg/cm²`:<span style={{color:'var(--warning-text)'}}>Not set — go to Setup</span>}
                      </span>
                    </div>
                    <span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>API Limit (ppm) = MAR × Rinse SA (cm²) ÷ Rinse Vol (mL) — varies per part</span>
                  </div>

                  <FloatingScrollWrapper>
                    <table style={{minWidth: detergentEnabled ? '2030px' : '1680px'}}>
                      <thead>
                        <tr>
                          <th style={{minWidth:'36px',textAlign:'center',padding:'5px 6px',fontSize:'var(--fs-sm)'}}>#</th>
                          <th style={{minWidth:'150px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Equipment</th>
                          <th style={{minWidth:'130px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Part Name</th>
                          <th style={{minWidth:'120px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Rinsing Solvent</th>
                          <th rowSpan={2} style={{minWidth:'160px',padding:'5px 8px',fontSize:'var(--fs-sm)',verticalAlign:'bottom'}} title="Manual reference + PDF for now — will link to LIMS">AMV Reference</th>
                          <th colSpan={3} style={{textAlign:'center',background:'color-mix(in srgb, #2563eb 8%, var(--bg-color))',borderBottom:'none',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Chemical — API Residue</th>
                          {detergentEnabled && (
                            <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #f59e0b 10%, var(--bg-color))',borderBottom:'none',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>
                              Chemical — Detergent Residue
                            </th>
                          )}
                          <th colSpan={3} style={{textAlign:'center',background:'color-mix(in srgb, #6366f1 8%, var(--bg-color))',borderBottom:'none',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Chemical — Solvent Quality</th>
                          <th colSpan={2} style={{textAlign:'center',background:'color-mix(in srgb, #dc2626 8%, var(--bg-color))',borderBottom:'none',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Microbial</th>
                          <th style={{minWidth:'160px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Notes</th>
                          <th style={{minWidth:'240px',padding:'5px 8px',fontSize:'var(--fs-sm)'}}>Remarks</th>
                        </tr>
                        <tr>
                          <th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/>
                          <th style={{minWidth:'100px',background:'color-mix(in srgb, #2563eb 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Rinse SA (cm²)</th>
                          <th style={{minWidth:'100px',background:'color-mix(in srgb, #2563eb 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Rinse Vol (mL)</th>
                          <th style={{minWidth:'130px',background:'color-mix(in srgb, #7c3aed 8%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>
                            API Limit (ppm)
                            <div style={{fontSize:'var(--fs-xs)',fontWeight:400,color:'#7c3aed'}}>= {marPerCm2||'MAR'} × SA ÷ Vol</div>
                          </th>
                          {detergentEnabled && <>
                            <th style={{minWidth:'140px',background:'color-mix(in srgb, #f59e0b 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>
                              Det. Limit (ppm)
                              <div style={{fontSize:'var(--fs-xs)',fontWeight:400,color:'#b45309'}}>= {detergentMarPerCm2||'Det.MAR'} × SA ÷ Vol</div>
                            </th>
                            <th style={{minWidth:'120px',background:'color-mix(in srgb, #f59e0b 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Visual Limit</th>
                          </>}
                          <th style={{minWidth:'105px',background:'color-mix(in srgb, #6366f1 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>TOC</th>
                          <th style={{minWidth:'105px',background:'color-mix(in srgb, #6366f1 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>pH Range</th>
                          <th style={{minWidth:'120px',background:'color-mix(in srgb, #6366f1 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Conductivity</th>
                          <th style={{minWidth:'100px',background:'color-mix(in srgb, #dc2626 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>BET</th>
                          <th style={{minWidth:'110px',background:'color-mix(in srgb, #dc2626 5%, var(--bg-color))',padding:'3px 8px',fontSize:'var(--fs-sm)'}}>Microbial</th>
                          <th style={{padding:'3px 6px'}}/><th style={{padding:'3px 6px'}}/>
                        </tr>
                      </thead>
                      <tbody>
                        {rinseRows.map((row,idx)=>{
                          const limit    = calcRinseLimit(row, marPerCm2);
                          const detLimit = calcDetRinseLimit(row);
                          const solvent  = getSolventData(row.rinse_solvent);
                          return (
                            <tr key={row.part_id||idx} style={{height:'34px'}}>
                              <td style={{textAlign:'center',color:'var(--text-secondary)',fontSize:'var(--fs-sm)',padding:'3px 6px'}}>{idx+1}</td>
                              <td style={{padding:'3px 8px'}}><span style={{fontSize:'var(--fs-sm)',fontWeight:600}}>{row.equipment_name}</span></td>
                              <td style={{padding:'3px 8px'}}><span style={{fontSize:'var(--fs-sm)'}}>{row.part_name}</span></td>
                              <td style={{padding:'3px 6px'}}>
                                {canEdit
                                  ? <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'2px 4px',width:'115px'}}
                                      value={row.rinse_solvent} onChange={e=>updateRinse(idx,'rinse_solvent',e.target.value)}>
                                      <option value="">— Select —</option>
                                      {solvents.map(s=><option key={s.id} value={s.solvent_name}>{s.solvent_name}</option>)}
                                    </select>
                                  : <span style={{fontSize:'var(--fs-sm)'}}>{row.rinse_solvent||'—'}</span>}
                              </td>
                              <td style={{padding:'3px 6px'}}>
                                <AmvCell reference={row.amv_reference} files={row.amv_files||[]} disabled={!canEdit}
                                  uploading={uploadingAmv?.type==='rinse' && uploadingAmv?.idx===idx}
                                  onReference={v=>updateRinse(idx,'amv_reference',v)}
                                  onUpload={f=>handleAmvUpload('rinse',idx,f)}
                                  onRemove={fn=>handleAmvRemove('rinse',idx,fn)}/>
                              </td>
                              <td style={{background:'color-mix(in srgb, #2563eb 3%, var(--card-bg))',padding:'3px 6px'}}>
                                <Num value={row.rinse_sa} onChange={v=>updateRinse(idx,'rinse_sa',v)} disabled={!canEdit} placeholder="2500" width="88px"/>
                                {row.surface_area_ref&&<div style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>Ref: {row.surface_area_ref}</div>}
                              </td>
                              <td style={{background:'color-mix(in srgb, #2563eb 3%, var(--card-bg))',padding:'3px 6px'}}>
                                <Num value={row.rinse_vol} onChange={v=>updateRinse(idx,'rinse_vol',v)} disabled={!canEdit} placeholder="5000" width="80px"/>
                              </td>
                              <td style={{...calcCell,padding:'3px 8px'}}>
                                {limit!==null
                                  ? <span style={{color:'#7c3aed'}}>{fmtLimit(limit)} ppm</span>
                                  : <span style={{color:'var(--text-secondary)',fontWeight:400,fontSize:'var(--fs-sm)'}}>Enter SA & Vol</span>}
                              </td>
                              {detergentEnabled && <>
                                <td style={{background:'color-mix(in srgb, #f59e0b 6%, var(--card-bg))',padding:'3px 8px'}}>
                                  {detLimit !== null
                                    ? <span style={{fontSize:'var(--fs-sm)',fontWeight:700,color:'#b45309'}}>{fmtLimit(detLimit)} ppm</span>
                                    : <span style={{fontSize:'var(--fs-sm)',color:'#b45309'}}>
                                        {detergentMarPerCm2 ? 'Enter SA & Vol' : 'Set Det. MAR ↑'}
                                      </span>}
                                </td>
                                <td style={{background:'color-mix(in srgb, #f59e0b 4%, var(--card-bg))',padding:'3px 8px'}}>
                                  {canEdit
                                    ? <select className="form-control" style={{fontSize:'var(--fs-sm)',padding:'2px 4px',width:'90px'}}
                                        value={row.visual_limit || 'Pass'} onChange={e=>updateRinse(idx,'visual_limit',e.target.value)}>
                                        <option value="Pass">{visualLabels.pass}</option>
                                        <option value="Fail">{visualLabels.fail}</option>
                                      </select>
                                    : <span style={{fontSize:'var(--fs-sm)',fontWeight:600,color:row.visual_limit==='Fail'?'var(--danger-color)':'var(--success-color)'}}>{row.visual_limit==='Fail' ? visualLabels.fail : visualLabels.pass}</span>}
                                </td>
                              </>}
                              {[
                                [solvent,`${solvent?.toc_limit??''} ${solvent?.toc_unit??''}`,'#6366f1'],
                                [solvent,solvent?`${solvent.ph_min}–${solvent.ph_max}`:'','#6366f1'],
                                [solvent,`${solvent?.conductivity_limit??''} ${solvent?.conductivity_unit??''}`,'#6366f1'],
                                [solvent,`${solvent?.bet_limit??''} ${solvent?.bet_unit??''}`,'#dc2626'],
                                [solvent,`${solvent?.microbial_limit??''} ${solvent?.microbial_unit??''}`,'#dc2626'],
                              ].map(([s,val,color],ci)=>(
                                <td key={ci} style={{background:`color-mix(in srgb, ${color} 4%, var(--card-bg))`,padding:'3px 8px'}}>
                                  {s?<span style={{fontSize:'var(--fs-sm)',fontWeight:600,color}}>{val.trim()||'—'}</span>
                                    :<span style={{fontSize:'var(--fs-sm)',color:'var(--text-secondary)'}}>—</span>}
                                </td>
                              ))}
                              <td style={{padding:'3px 6px'}}>
                                <input className="form-control" style={{width:'150px',fontSize:'var(--fs-sm)',padding:'2px 6px'}}
                                  value={row.rinse_notes} disabled={!canEdit}
                                  onChange={e=>updateRinse(idx,'rinse_notes',e.target.value)} placeholder="e.g. grouped with…"/>
                              </td>
                              <td style={{padding:'3px 6px'}}>
                                <textarea className="form-control" rows={1} style={{width:'230px',fontSize:'var(--fs-sm)',padding:'2px 6px',resize:'vertical'}}
                                  value={row.remarks} disabled={!canEdit}
                                  onChange={e=>updateRinse(idx,'remarks',e.target.value)} placeholder="Remarks…"/>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </FloatingScrollWrapper>
                  <ActionBar/>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ChangeReferenceModal
        open={changeRefGate}
        qmsLinked={qmsLinked}
        busy={changeRefBusy}
        recordLabel={record ? `${record.equipment_name || ''}${record.part_name ? ' / ' + record.part_name : ''}` : ''}
        onCancel={() => setChangeRefGate(false)}
        onConfirm={confirmChangeReference}
      />

      {/* ── E-Sign Modal ──────────────────────────────────────────────────────── */}
      {showESign&&(
        <div className="modal-overlay" style={{backdropFilter:'blur(3px)',backgroundColor:'rgba(0,0,0,0.5)'}}
          onClick={e=>{if(e.target===e.currentTarget)closeESign();}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:'500px',width:'95%'}}>
            <div className="modal-header">
              <h2 className="modal-title" style={{fontSize:'var(--fs-xl)'}}>{eSignTitle}</h2>
              <button className="modal-close" onClick={closeESign}>×</button>
            </div>
            <div className="modal-body">
              {(eSignMode==='product'||eSignMode==='qa')&&(
                <div className="form-group">
                  <label className="form-label">Decision *</label>
                  <select className="form-control" value={eSignData.decision}
                    onChange={e=>setESignData(p=>({...p,decision:e.target.value}))}>
                    {APPROVAL_DECISIONS.map(d=><option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Login Password *</label>
                <input ref={passwordRef} type="password" className="form-control"
                  autoComplete="new-password" data-lpignore="true"
                  value={eSignData.password}
                  onChange={e=>setESignData(p=>({...p,password:e.target.value}))}
                  placeholder="Enter your login password"
                  onKeyDown={e=>{if(e.key==='Enter')handleConfirmESign();}}/>
              </div>
              <div className="form-group">
                <label className="form-label">Reason / Comment *</label>
                <textarea className="form-control" rows={3} value={eSignData.comment}
                  onChange={e=>setESignData(p=>({...p,comment:e.target.value}))}
                  placeholder="Provide reason or comment…"/>
              </div>
              <div style={{padding:'8px 12px',background:'var(--bg-color)',borderRadius:'6px',fontSize:'var(--fs-sm)',color:'var(--text-secondary)',border:'1px solid var(--border-color)'}}>
                <strong>Signed by:</strong> {user.username||'Unknown'} &nbsp;|&nbsp;
                <strong>Date/Time:</strong> {new Date().toLocaleString()} &nbsp;|&nbsp;
                <strong>Role:</strong> {user.role}
              </div>
            </div>
            <div className="modal-footer" style={{display:'flex',justifyContent:'flex-end',gap:'10px',marginTop:'12px'}}>
              <button className="btn btn-outline" onClick={closeESign} disabled={eSigning}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmESign} disabled={eSigning}>
                {eSigning?'Processing…':(
                  eSignMode==='draft'         ?'Confirm & Save':
                  eSignMode==='submit'        ?'Confirm & Submit':
                  eSignMode==='returnToDraft' ?'Confirm Return to Draft':
                  eSignMode==='delete'        ?'Confirm Deactivate':
                  eSignMode==='product'       ?'Confirm Product Decision':
                                               'Confirm QA Decision'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <VersionHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={record ? `Version History — ${record.production_line || record.plant_code}` : ''}
        fetcher={() => acceptanceCriteriaAPI.getRevisions(record.id)}
      />
    </div>
  );
};

export default AcceptanceCriteria;
